import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { onAuthStateChanged, type User as FbUser } from 'firebase/auth'
import { auth, signOut } from './firebase'
import { api } from './api'
import { completeSightHubSignIn } from './sso'
import type { Role } from './types'
import { isAdmin, isStaff } from './types'

interface AuthState {
  /** `undefined` while Firebase restores the session; `null` when anonymous. */
  user: FbUser | null | undefined
  email: string | null
  /** Display name from `GET /me`; the backend guarantees it is never empty. */
  name: string | null
  role: Role | null
  /** True until we know both the Firebase session and the backend role. */
  loading: boolean
  /** The backend was unreachable, so the role is unknown even though sign-in worked. */
  error: string | null
  staff: boolean
  admin: boolean
  /**
   * Open the sign-in chooser, SightHub or Google. Nobody is signed in until they pick
   * one — a session restored on load is only ever one they picked before.
   */
  signIn: () => void
  chooserOpen: boolean
  /** Why the SightHub sign-in this page came back from failed; cleared with the chooser. */
  ssoError: string | null
  closeChooser: () => void
  signOut: () => Promise<void>
  /**
   * Re-read the role and mint a token carrying the current `role` claim.
   * The backend rewrites the claim on any authenticated request where it has
   * drifted from Postgres, but the token already in hand keeps the stale claim
   * until it is refreshed — so a role change only takes effect after this.
   */
  refresh: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FbUser | null | undefined>(undefined)
  const [role, setRole] = useState<Role | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [name, setName] = useState<string | null>(null)
  const [resolvingRole, setResolvingRole] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chooserOpen, setChooserOpen] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let unsubscribe = () => {}

    const onUser = async (next: FbUser | null) => {
      setUser(next)
      setError(null)

      if (!next) {
        setRole(null)
        setEmail(null)
        setName(null)
        return
      }

      setResolvingRole(true)
      try {
        const me = await api.me()
        if (cancelled) return
        setRole(me.role)
        setEmail(me.email)
        setName(me.name ?? me.email)
        // /me writes the `role` custom claim. The token we hold predates that
        // write, so refresh before anything touches Storage — stale claim reads
        // as non-staff and internal pages come back permission-denied.
        await next.getIdToken(true)
      } catch (e) {
        if (cancelled) return
        setRole(null)
        setEmail(next.email)
        // The backend never answered, so fall back to what the Firebase account says.
        setName(next.displayName ?? next.email)
        setError(e instanceof Error ? e.message : 'Could not load your role.')
      } finally {
        if (!cancelled) setResolvingRole(false)
      }
    }

    // A SightHub sign-in coming back in the URL settles before anything listens:
    // the restored Google session must not get to report itself first.
    void completeSightHubSignIn().then((failure) => {
      if (cancelled) return
      if (failure) {
        setSsoError(failure)
        setChooserOpen(true)
      }
      unsubscribe = onAuthStateChanged(auth, onUser)
    })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  const openChooser = useCallback(() => setChooserOpen(true), [])

  const closeChooser = useCallback(() => {
    setChooserOpen(false)
    setSsoError(null)
  }, [])

  const doSignOut = useCallback(async () => {
    await signOut()
    setRole(null)
    setEmail(null)
    setName(null)
  }, [])

  const refresh = useCallback(async () => {
    const current = auth.currentUser
    if (!current) return

    setResolvingRole(true)
    try {
      const me = await api.me()
      setRole(me.role)
      setEmail(me.email)
      setName(me.name ?? me.email)
      await current.getIdToken(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not refresh your role.')
    } finally {
      setResolvingRole(false)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      email,
      name,
      role,
      loading: user === undefined || resolvingRole,
      error,
      staff: isStaff(role),
      admin: isAdmin(role),
      signIn: openChooser,
      chooserOpen,
      ssoError,
      closeChooser,
      signOut: doSignOut,
      refresh,
    }),
    [
      user,
      email,
      name,
      role,
      resolvingRole,
      error,
      openChooser,
      chooserOpen,
      ssoError,
      closeChooser,
      doSignOut,
      refresh,
    ],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
